import './styles/tokens.css';
import './styles/base.css';
import './styles/button.css';
import './styles/shell.css';
import './styles/surface.css';
import './styles/form.css';
import './styles/nav.css';
import './styles/icons.css';
import './styles/health.css';
import './styles/login.css';
import './styles/sessions.css';
import './styles/me.css';
import './styles/account-menu.css';
import './styles/locale-switcher.css';
import './styles/comingsoon.css';
import './styles/dialog.css';
import './styles/company.css';
import './styles/passkey.css';
import './styles/passkeys.css';
import './styles/recovery.css';
import './styles/error.css';
import './styles/admin.css';
import './styles/trips.css';
import './styles/trip-mode.css';
import './styles/ingest.css';
import './styles/prompt-surface.css';
import './styles/ingest-entry.css';
import './styles/utilities.css';

import './styles/update-prompt.css';

import { AppShell } from './components/AppShell';
import { watchForUpdate } from './services/swUpdate';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { ErrorScreen } from './pages/ErrorScreen';
import {
  initRouter,
  lazy,
  navigate,
  refreshRoute,
  registerRoute,
  setAuthGuard,
  setNotFound,
  type Component,
} from './router';
import { authInit, isExplicitlySignedOut, isAuthenticated, currentUser, consumeForcedReauth } from './services/auth';
import { canViewPath, RBAC_CONTEXT_EVENT } from './services/capabilities';
import { guardDecision, isSessionLessRoute, shouldBootstrapSession } from './services/routeGuard';
import { shellEntryCapable } from './services/shellCapability';
import { t, initLocale, ensureCatalogLoaded, ensureAdminI18nLoaded, ensureAiI18nLoaded, LOCALE_CHANGED_EVENT } from './i18n';
import { checkHealth } from './api/client';
import { api } from './services/api';
import { promptPasskeyEnroll } from './components/PasskeyModal';
import { ensureCompanyChosen } from './components/CompanyPicker';
import { getDeviceClass, setRootDeviceClass } from './utils/viewport';
import { setRootSurface } from './utils/surface';
import { initErrlog } from './utils/errlog';
import { getStoredTheme, setTheme } from './utils/theme';
import { startExpirySweep } from './services/offline';

function wrap(inner: Component): Component {
  return new AppShell(inner);
}

// OAuth callbacks land on a normal route, not a dedicated page: the
// vfco-auth-package callback redirects to {redirect}?auth=success (the refresh
// cookie is already set, so authInit() below silently exchanges it for a token)
// or {redirect}?auth=error&code=CODE on failure. Normalize the failure to
// /login?error=CODE so LoginPage renders the message immediately — that path
// has no gated bootstrap to wait on. Runs before routing.
//
// Returns whether this was a `?auth=success` landing — the boot IIFE below
// needs that to override the signed-out gate (issue 444): the refresh cookie
// was just set server-side, so the bootstrap must still run once here even if
// this browser is (still) marked explicitly signed out from an earlier
// session.
//
// Deliberately does NOT strip the `?auth=success` marker itself (PR 471
// review, finding 3): the marker is this boot's only record that an OAuth
// exchange is in flight, and authInit() below can fail transiently (a 5xx
// during a revision swap, a network drop) with no session established.
// Stripping it unconditionally here would lose that record — a reload would
// then land as a fully anonymous view with no override, never retrying the
// exchange, even though the server-set refresh cookie is still sitting there
// valid. clearOAuthSuccessMarker() below strips it instead, and only once
// authInit() has actually installed a session.
function handleOAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  const auth = params.get('auth');
  if (!auth) return false;
  if (auth === 'error') {
    const code = params.get('code') ?? 'OAUTH_PROVIDER_ERROR';
    window.history.replaceState({}, '', `/login?error=${encodeURIComponent(code)}`);
    return false;
  }
  return true;
}

// Strips the `?auth=success` (and `code`) marker from the URL. Called by the
// boot IIFE only after authInit() has actually installed a session for an
// OAuth-success landing (isAuthenticated() true) — see handleOAuthCallback's
// doc comment above for why this can't run any earlier than that.
function clearOAuthSuccessMarker(): void {
  const params = new URLSearchParams(window.location.search);
  params.delete('auth');
  params.delete('code');
  const qs = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
}

// On first desktop sign-in for a sensitive role, offer passkey enrollment
//. Best-effort and non-blocking: the GET status poll already ran for
// the SPA, and any failure here must never wedge boot. Phones are exempt, so we
// skip the poll entirely off desktop.
async function maybePromptPasskeyEnroll(): Promise<void> {
  if (!isAuthenticated() || getDeviceClass() !== 'desktop') return;
  try {
    const status = await api<{ webauthn?: { enrollmentRequired?: boolean } }>('/api/auth/step-up');
    if (status.webauthn?.enrollmentRequired) {
      await promptPasskeyEnroll();
    }
  } catch {
    // No prompt on a failed poll — enrollment can be offered from settings later.
  }
}

// Drain the offline sync queue for the signed-in worker. Best-effort
// and never wedges boot: drainSyncQueue itself no-ops when offline or already
// draining, and a transient/auth failure leaves the queue for the next trigger.
// Fired on boot, on the `online` event, and when the service worker's Background
// Sync wakes the page (the 'sync-drain' message). The drain is a lazy chunk:
// nothing on first paint needs it, and it is where the proof-upload path
// (DATA-015) hangs — a boot with nothing queued still loads it once, but
// never ahead of the shell.
function drainSync(): void {
  const workerId = currentUser()?.userId;
  if (!workerId) return;
  void import('./services/sync')
    .then((m) => m.drainSyncQueue(workerId))
    .catch(() => undefined);
}

initErrlog();
setRootDeviceClass();
setRootSurface();
setTheme(getStoredTheme());
// Purge expired offline PII on boot, then hourly. Auth-independent —
// the ~7-day retention is enforced even on a signed-out load.
startExpirySweep();

registerRoute('/', () => wrap(HomePage()));
// The one remaining placeholder route (/reports) lazy-loads ComingSoonPage into
// its own tiny chunk WITH its strings riding the desk-slice (the 94c pattern) — a
// not-yet-built module's placeholder has no business in the first-load bundle
// every phone pays for (#94; the split bought the GPS-010 catalog's main.js
// headroom). placeholder is declared below adminPage but referenced only at
// navigation time.
const placeholder = (icon: 'reports', key: 'reports'): Component =>
  adminPage(async () =>
    (await import('./pages/ComingSoonPage')).ComingSoonPage({
      icon,
      title: t(`comingSoon.${key}.title`),
      copy: t(`comingSoon.${key}.copy`),
    }));
// The work-order list + form (FE-028). Three routes, all on the lazy admin
// slice; the module gate is prefix-aware (MODULE_FOR_PATH['/work-orders']), so
// /new and /:id inherit it. `:id` is captured before lazy() — the loader
// receives no params.
registerRoute('/work-orders', () =>
  wrap(adminPage(async () => (await import('./pages/WorkOrdersPage')).WorkOrdersPage())));
registerRoute('/work-orders/new', () =>
  wrap(adminPage(async () => (await import('./pages/WorkOrderFormPage')).WorkOrderFormPage(null))));
registerRoute('/work-orders/:id', (params) => {
  const id = params.id;
  return wrap(adminPage(async () => (await import('./pages/WorkOrderFormPage')).WorkOrderFormPage(id)));
});
// The ingest review/confirm screen (FE-002): a param route under the
// work-orders gate. aiPage awaits the ai.* slice on top of adminPage's admin
// slice — the page reuses the leg builder, whose copy rides workOrders.*.
// It cannot collide with /work-orders/:id: a param never matches a slash.
registerRoute('/work-orders/ingest/:id', (params) => {
  const id = params.id;
  return wrap(aiPage(async () => (await import('./pages/IngestReviewPage')).IngestReviewPage(id)));
});
// The trips module (FE-029): `/trips` is the trip RECORD list (`trips.list`),
// `/trips/live` the GPS command center GPS-010 built there and FE-029 re-homed
// (`trips.command_center` via capabilities.ts LEAF_FOR_PATH), `/trips/review`
// the FE-031 decision surface (`trips.review_queue` — approve composed with
// the device ceiling, so a phone never reaches it and a tablet does), and
// `/trips/:id` the record detail (`trips.detail`). All lazy via adminPage:
// their strings ride the desk-only lazy i18n slice, and the wall's Maps CDN
// script stays out of the first-load bundle. ORDER MATTERS — the router takes
// the first pattern that matches, so the two exact paths are registered
// before the `:id` pattern (a param matches any single segment).
registerRoute('/trips', () =>
  wrap(adminPage(async () => (await import('./pages/TripsListPage')).TripsListPage())));
registerRoute('/trips/live', () =>
  wrap(adminPage(async () => (await import('./pages/TripsPage')).TripsPage())));
registerRoute('/trips/review', () =>
  wrap(adminPage(async () => (await import('./pages/ReviewQueuePage')).ReviewQueuePage())));
registerRoute('/trips/:id', (params) => {
  const id = params.id;
  return wrap(adminPage(async () => (await import('./pages/TripDetailPage')).TripDetailPage(id)));
});
registerRoute('/reports', () => wrap(placeholder('reports', 'reports')));
// The admin section. Two routes rather than one tabbed hub, so each carries its
// own module capability: an ops_manager holding admin_users but not admin_roles
// reaches Members and is correctly denied Roles.
//
// Loaded lazily: these are desk-only, permission-gated screens, so their code has
// no business in the first-load bundle every phone pays for.
//
// adminPage additionally awaits the admin i18n slice (issue 182 / 94a; extended in
// 94c) alongside the page chunk, so the admin strings are registered before the page
// mounts and no t() renders a missing-key fallback. Both loads run concurrently. Every
// admin route uses it: 94c moved the console/activity/config/errors/security lenses'
// camelCase namespaces into the same lazy slice, so they await it here too rather than
// riding main.js on a plain lazy().
const adminPage = (load: () => Promise<Component>): Component =>
  lazy(async () => {
    // Load the admin i18n slice and the page chunk concurrently. A failed slice
    // load is swallowed (mirrors the es/pt boot loader) so the page still mounts —
    // it renders the en fallback rather than wedging on a blank placeholder — and
    // loadAdminFor clears its in-flight slot, so the next admin navigation retries.
    const [, page] = await Promise.all([ensureAdminI18nLoaded().catch(() => undefined), load()]);
    return page;
  });
// aiPage is adminPage plus the lazy ai.* slice (FE-002). Same swallow-on-fail
// posture: the page still mounts on the en fallback.
const aiPage = (load: () => Promise<Component>): Component =>
  adminPage(async () => {
    const [, page] = await Promise.all([ensureAiI18nLoaded().catch(() => undefined), load()]);
    return page;
  });
registerRoute('/admin/roles', () =>
  wrap(adminPage(async () => (await import('./pages/AdminRolesPage')).AdminRolesPage())));
registerRoute('/admin/users', () =>
  wrap(adminPage(async () => (await import('./pages/AdminUsersPage')).AdminUsersPage())));
registerRoute('/admin/subdivisions', () =>
  wrap(adminPage(async () => (await import('./pages/AdminSubdivisionsPage')).AdminSubdivisionsPage())));
// The counterparty catalog manager (issue 544): the screen that lets an operator
// populate the party catalog without calling the API by hand. Gated on the
// `parties` module (capabilities.ts); same adminPage lazy pattern, so the page
// chunk and its admin.parties i18n stay off every other route.
registerRoute('/admin/parties', () =>
  wrap(adminPage(async () => (await import('./pages/AdminPartiesPage')).AdminPartiesPage())));
// Company-plane Activity lens (API-007.A): the audit trail + member sign-in feed.
// adminPage like the other admin routes — code + its adminActivity i18n slice stay
// out of the first-load bundle (94c).
registerRoute('/admin/activity', () =>
  wrap(adminPage(async () => (await import('./pages/AdminActivityPage')).AdminActivityPage())));
registerRoute('/admin/companies', () =>
  wrap(adminPage(async () => (await import('./pages/AdminCompaniesPage')).AdminCompaniesPage())));
// Platform-plane lenses (API-006): HQ-gated, desk-only Status + Security
// consoles. adminPage like the other admin routes — their code and their
// adminStatus/adminSecurity i18n slices stay out of the first-load bundle (94c).
registerRoute('/admin/status', () =>
  wrap(adminPage(async () => (await import('./pages/AdminStatusPage')).AdminStatusPage())));
registerRoute('/admin/security', () =>
  wrap(adminPage(async () => (await import('./pages/AdminSecurityPage')).AdminSecurityPage())));
// The HQ-roster lens (issue 309): the read surface for standing all-company
// access. Platform-gated like Status/Security; same adminPage lazy pattern.
registerRoute('/admin/hq-roster', () =>
  wrap(adminPage(async () => (await import('./pages/AdminHqRosterPage')).AdminHqRosterPage())));
// Platform-plane Config lens + errlog viewer (API-007.B): the read-only settings /
// RBAC-matrix / collection inventory and the persisted error-aggregate browser.
// Same adminPage pattern — code + adminConfig/adminErrors i18n out of the first-load bundle (94c).
registerRoute('/admin/config', () =>
  wrap(adminPage(async () => (await import('./pages/AdminConfigPage')).AdminConfigPage())));
registerRoute('/admin/errors', () =>
  wrap(adminPage(async () => (await import('./pages/AdminErrorLogPage')).AdminErrorLogPage())));
// The fleet lens (FE-032): feed health, device roster, per-vehicle history and
// the Maps view over the GPS capture corpus (API-015). Same platform gate and
// adminPage lazy pattern — the page chunk, its own adminFleet.* i18n slice
// (i18n/fleetSlice.ts, awaited by the page factory), and the Maps
// CDN script (loaded lazily inside the chunk) all stay off every other route.
registerRoute('/admin/fleet', () =>
  wrap(adminPage(async () => (await import('./pages/AdminFleetPage')).AdminFleetPage())));
// The location manager (FE-027): the trip domain's first master-data screen —
// catalog, map-based coordinate/geofence authoring, aliases and the unresolved
// review queue. Gated on the `locations` module (capabilities.ts); adminPage
// lazy pattern, so the page chunk, its admin.locations i18n and the Maps CDN
// script (loaded inside the chunk) all stay off every other route.
registerRoute('/admin/locations', () =>
  wrap(adminPage(async () => (await import('./pages/AdminLocationsPage')).AdminLocationsPage())));
// The catalog admin (FE-030): the per-company vocabulary catalogs (statuses,
// reason codes, cargo kinds), the city reference catalog, routes + route
// grants (issue 671) and the per-party synonym maps. Gated on the `catalogs`
// module (capabilities.ts); adminPage lazy pattern, and the page factory
// awaits its OWN admin.catalogs.* slice (i18n/catalogsSlice.ts — issue 671
// split it out of the admin slice, and its loader rides the page chunk so the
// first load carries no bytes for it).
registerRoute('/admin/catalogs', () =>
  wrap(adminPage(async () => (await import('./pages/AdminCatalogsPage')).AdminCatalogsPage())));
// The Me hub follows its satellite settings pages into a lazy chunk (the
// pattern below): it is reached only via the account menu, never on boot or
// a primary tab — and the GPS-011 additions put the eager shell at the gzip
// budget's edge, so the split pays for the driver surface's main.js share.
registerRoute('/me', () => wrap(lazy(async () => (await import('./pages/MePage')).MePage())));
// The account-settings pages are reached only via the account menu / Me hub —
// never on boot or a primary tab — so they lazy-load into their own chunk (the
// established admin-route pattern). This keeps them out of the eager shell bundle,
// the room the FE-011 Admin dropdown needs under the gzip budget (durable
// shell-splitting tracked in #94). PasskeysPage shares formatLastUsed from
// SessionsPage, so the two co-split cleanly.
registerRoute('/settings/devices', () =>
  wrap(lazy(async () => (await import('./pages/SessionsPage')).SessionsPage())));
registerRoute('/settings/passkeys', () =>
  wrap(lazy(async () => (await import('./pages/PasskeysPage')).PasskeysPage())));
registerRoute('/login', () => new LoginPage(), true);
// /auth/verify and /recovery are public, pre-auth, and reached at most once per
// user journey — /auth/verify only by clicking a magic link, /recovery only after
// losing a passkey — so neither belongs in the bundle every visitor downloads on
// first paint. Same shape as /invite below: lazy, public, not AppShell-wrapped.
// /login stays eager because it IS the first paint for an unauthenticated visitor.
//
// A failed chunk lands on lazy()'s chunkErrorScreen (reload-to-retry), which
// matters more here than on an admin route: /auth/verify is the sign-in path, so
// the failure has to be legible rather than a blank card.
registerRoute(
  '/auth/verify',
  () => lazy(async () => new (await import('./pages/AuthVerifyPage')).AuthVerifyPage()),
  true,
);
registerRoute(
  '/recovery',
  () => lazy(async () => new (await import('./pages/RecoveryPage')).RecoveryPage()),
  true,
);
// The invitee landing bridge (FE-008). Public — the invitee has no session yet —
// and lazy, so this pre-auth page's code stays out of the signed-in bundle. Not
// AppShell-wrapped: like /auth/verify it renders its own card while it redeems
// the token and bridges into the standard verify → bind sign-in.
registerRoute(
  '/invite',
  () => lazy(async () => new (await import('./pages/InviteAcceptPage')).InviteAcceptPage()),
  true,
);
// The driver trip-mode surface (GPS-011). Public — the driver has no app
// session, the trip-scoped token in the URL fragment is the only
// credential — and lazy, so the driver chunk (page + trip.* i18n slice)
// never rides main.js. Not AppShell-wrapped: like /invite it renders its
// own full-bleed capture stack.
registerRoute(
  '/trip',
  () => lazy(async () => new (await import('./pages/TripModePage')).TripModePage()),
  true,
);

setNotFound(() => wrap(new ErrorScreen({ kind: 'notFound' })));
// The decision lives in services/routeGuard (pure, and therefore testable); this
// only renders it. The forbidden screen needs the app shell, which is why that
// half stays here rather than moving into a service module.
setAuthGuard((path) => {
  const decision = guardDecision(path, isAuthenticated(), canViewPath);
  switch (decision.kind) {
    case 'redirect':
      return { allowed: false, redirectTo: decision.to };
    case 'forbidden':
      return { allowed: false, render: () => wrap(new ErrorScreen({ kind: 'forbidden' })) };
    default:
      return { allowed: true };
  }
});

const app = document.getElementById('app');
if (app) {
  void (async () => {
    // The driver trip-mode route (GPS-011) is session-LESS by contract: its only
    // credential is the trip-scoped token in the URL fragment, and it calls
    // tracking directly, never an app API. So it must boot WITHOUT the session
    // machinery below — every piece of which is actively hostile to it:
    //   · the forced-reauth rewrite would replace the URL with /login and destroy
    //     the fragment token (observed live: any phone carrying prior Fexo state
    //     — a driver who also uses the app, a shared cab phone, a device-class
    //     change — lost its trip link);
    //   · ensureCompanyChosen() could raise a company picker over a driver's trip;
    //   · authInit()/drainSync() are signed-in-worker paths that only 401 here.
    // The route still renders through the normal router + guard (it is in
    // PUBLIC_PATHS); this only skips the session bootstrap. The predicate lives
    // in routeGuard so it is testable — app.ts is boot side effects no test can
    // import (routeGuard.ts's own rationale).
    // GPS-012: inside the Capacitor shell, resolve the entry route BEFORE
    // anything reads location. A cold-start deep link must land on /trip
    // with its fragment token intact, and a bare shell open belongs on
    // /trip too — the only surface the shell serves in Phase A; a Tier-1
    // driver has no account, so /login is a wall, not a door. Gated on the
    // App-plugin capability probe (shellCapability — never UA), and the
    // Capacitor runtime rides the lazy shellNative chunk so the pure PWA
    // bundle never loads it (GPS-012.6). A failed chunk load boots the
    // normal web path — degradation, not damage.
    if (shellEntryCapable()) {
      await import('./services/shellNative')
        .then((shell) => shell.resolveShellEntry())
        .catch(() => undefined);
    }
    const sessionLessRoute = isSessionLessRoute(location.pathname);

    if (!sessionLessRoute) {
      const oauthSuccessLanding = handleOAuthCallback();
      // The session-establishing probes (GET /api/me, POST /api/auth/refresh)
      // only run when this browser might actually have a session — the
      // ABSENCE of an explicit signed-out flag (auth.ts, redesigned per PR 471
      // review finding 2) — or this is the one boot that has to override
      // that gate regardless: the OAuth callback landing (issue 444). A
      // browser explicitly marked signed out skips both network calls
      // entirely instead of paying two logged 401s for nothing. Route-based
      // skipping was rejected: /login and /recovery still need authInit to
      // run whenever a session might exist, or the signed-in redirect-away
      // (issue 299) silently breaks.
      if (shouldBootstrapSession(isExplicitlySignedOut(), oauthSuccessLanding)) {
        await authInit();
      }
      // Only now — after the gated bootstrap this landing overrode has run to
      // completion — do we know whether it actually installed a session.
      // Clear the `?auth=success` marker on that confirmed success only (PR
      // 471 review, finding 3): a transient authInit() failure (5xx, network
      // drop) leaves the marker in the URL so a reload retries the exchange
      // instead of silently losing it and stranding the still-valid refresh
      // cookie.
      if (oauthSuccessLanding && isAuthenticated()) {
        clearOAuthSuccessMarker();
      }
      // A device-class change on refresh forces re-auth: authInit leaves no session
      // and the guard would bounce to /login with no explanation. Rewrite the URL so
      // LoginPage renders the "signed out for security" state instead (FE-013).
      // Mirrors handleOAuthCallback's replaceState — the router reads it below.
      if (!isAuthenticated() && consumeForcedReauth()) {
        window.history.replaceState({}, '', '/login?reason=security');
      }
    }
    // Fold the signed-in user's server-side locale preference into the active
    // locale now that authInit has populated it (FE-006.2). i18n already
    // self-resolved localStorage → navigator → en at import; this upgrades to the
    // user preference when present (currently always empty — no write path yet).
    const activeLocale = initLocale(currentUser()?.locale ?? null);
    // The es/pt catalogues are lazy-loaded chunks (issue 114): the locale resolved
    // synchronously above, but its content is a separate bundle. Load it before
    // the first route render so a persisted es/pt user sees translated copy on
    // first paint — no English flash. `en` is built in and resolves immediately.
    // A failed chunk load must never wedge boot — swallow it and proceed; the
    // locale simply renders through the en fallback (FE-006.3) until a later load.
    await ensureCatalogLoaded(activeLocale).catch(() => undefined);
    // OAuth / reload lands here; a multi-company session may still be unbound
    // after authInit (which auto-binds only a lone company). Present the picker
    // before routing so the first screen is scoped to a company (AUTH-001.3).
    if (!sessionLessRoute) await ensureCompanyChosen();
    initRouter(app);
    // An in-menu language switch persists the choice and broadcasts
    // LOCALE_CHANGED_EVENT (i18n). Remount the current route so the whole UI —
    // shell chrome + page — re-renders through the new catalogue without a page
    // reload (FE-010.3). navigate() to the same path re-renders in place.
    window.addEventListener(LOCALE_CHANGED_EVENT, () => {
      navigate(location.pathname + location.search);
    });
    // A company switch changes both what the page should show and whether the
    // user may see it, so rebuild the route (issues 297/298). Wired once, here,
    // rather than page by page: pages are one-shot, so the rebuild IS the reload,
    // and none of them can forget to subscribe. AppShell keeps its own listener
    // for the nav chrome, which is not part of the route.
    //
    // capabilities.publish() stores the new context before it broadcasts, so the
    // guard this rebuild re-runs reads the company being switched TO.
    window.addEventListener(RBAC_CONTEXT_EVENT, () => refreshRoute());
    void checkHealth();
    if (!sessionLessRoute) {
      void maybePromptPasskeyEnroll();

      // Sync any mutations captured offline: once now (a reload after an
      // offline session may have a queue), and again whenever the network returns.
      // The driver route has its own position queue draining to tracking — this
      // signed-in LWW path is not it (driver-app-spec §5.5).
      drainSync();
      window.addEventListener('online', drainSync);
    }

    if ('serviceWorker' in navigator) {
      // Background Sync wakes the page on reconnect via this message (sw.ts) —
      // the page holds the token + worker key the worker can't, so it drains.
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if ((event.data as { type?: string } | null)?.type === 'sync-drain' && !sessionLessRoute) {
          drainSync();
        }
      });
      navigator.serviceWorker.register('/sw.js').then(watchForUpdate).catch(() => {
        // Service worker registration is best-effort; failure leaves the
        // app fully functional online.
      });
    }
  })();
}

